import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { AnalysisAPIClient } from 'src/biz_spa/apiClients'
import type { SearchKeywordImpressionsAggregatesParams, SearchKeywordImpressionsPlotsParams } from 'src/biz_spa/apiClients'
import type { SearchKeywordImpressionAggregate, SearchKeywordImpressionPlot } from 'src/biz_spa/types'

export type FetchAggregatesParams = Omit<SearchKeywordImpressionsAggregatesParams, 'rawSearchKeywords'>

export const useSearchQueries = () => {
  const totalCount = ref(0)
  const all = ref<SearchKeywordImpressionAggregate>({
    fields: [],
    impressionCount: 0,
    locale: null,
    originalLocale: null,
    searchKeywordLabelBranded: null,
    searchKeywordLabelSelected: null,
  })
  const aggregates = ref<SearchKeywordImpressionAggregate[]>([])
  const plots = ref<SearchKeywordImpressionPlot[]>([])
  const filteredAggregatesCount = ref(0)

  const isAggregatesLoading = ref(false)
  const isPlotsLoading = ref(false)

  const apiClient = new AnalysisAPIClient()
  const route = useRoute()

  const companyUuid = computed(() => route.params.company_uuid as string)

  const fetchAggregates = async (params: FetchAggregatesParams) => {
    isAggregatesLoading.value = true
    try {
      const response = await apiClient.readSearchKeywordImpressionsAggregates(companyUuid.value, params)
      aggregates.value = response.data
      totalCount.value = response.meta.totalCount
      all.value = response.meta.all
    } finally {
      isAggregatesLoading.value = false
    }
  }

  const fetchComparedAggregates = async (params: FetchAggregatesParams) => {
    isAggregatesLoading.value = true
    const rawSearchKeywords = aggregates.value.map((aggregate) => aggregate.fields[0].id)
    try {
      const response = await apiClient.readSearchKeywordImpressionsAggregates(companyUuid.value, {
        ...params,
        rawSearchKeywords,
        // MEMO: 比較先のデータはrawSearchKeywordsを指定するため常に1ページ目を参照する
        page: 1,
        per: rawSearchKeywords.length,
      })

      all.value.growthRate = calculateGrowthRate(all.value.impressionCount, response.meta.all.impressionCount)
      all.value.comparisonImpressionCount = response.meta.all.impressionCount

      aggregates.value = aggregates.value.map((aggregate) => {
        const compareData = response.data.find((data) => data.fields[0].id === aggregate.fields[0].id)
        if (!compareData) return aggregate

        aggregate.growthRate = calculateGrowthRate(aggregate.impressionCount, compareData.impressionCount),
        aggregate.comparisonImpressionCount = compareData.impressionCount
        return aggregate
      })
    } finally {
      isAggregatesLoading.value = false
    }
  }

  const fetchAggregatesCount = async (params: FetchAggregatesParams) => {
    filteredAggregatesCount.value = await apiClient.readSearchKeywordImpressionsAggregatesCount(companyUuid.value, params)
  }

  const fetchPlots = async (params: SearchKeywordImpressionsPlotsParams, isCompared = false) => {
    isPlotsLoading.value = true
    try {
      const response = await apiClient.readSearchKeywordImpressionsPlots(companyUuid.value, params)
      response.data.isCompared = isCompared

      plots.value = [...plots.value, response.data]
    } finally {
      isPlotsLoading.value = false
    }
  }

  const resetComparedAggregates = () => {
    aggregates.value = aggregates.value.map((aggregate) => {
      const newValue = aggregate
      delete newValue.growthRate
      delete newValue.comparisonImpressionCount
      return newValue
    })
  }

  const resetComparedPlots = () => {
    plots.value = plots.value.filter((plot) => !plot.isCompared)
  }

  const resetCompared = () => {
    resetComparedAggregates()
    resetComparedPlots()
  }

  const resetPlots = () => {
    plots.value = []
  }

  const calculateGrowthRate = (current: number, previous?: number) => {
    if (!previous) return 0
    return Math.round(((current - previous) / previous) * 100)
  }

  return {
    aggregates: computed(() => aggregates.value),
    plots: computed(() => plots.value),
    filteredAggregatesCount: computed(() => filteredAggregatesCount.value),
    companyUuid: computed(() => companyUuid.value),
    all: computed(() => all.value),
    totalCount: computed(() => totalCount.value),
    isAggregatesLoading: computed(() => isAggregatesLoading.value),
    isPlotsLoading: computed(() => isPlotsLoading.value),
    fetchAggregates,
    fetchComparedAggregates,
    fetchAggregatesCount,
    fetchPlots,
    resetPlots,
    resetComparedAggregates,
    resetComparedPlots,
    resetCompared,
  }
}
